// Auth feature — typed wrappers around openapi-fetch.
// Usage example: `const session = await fetchSession()` — returns SessionView or throws ApiError.
import { apiClient } from "@/lib/api/client";
import { unwrap } from "@/lib/api/error";
import { asBrokerCurrency } from "@/lib/format/currency";
import type { components } from "@generated/api/schema";
import type {
  SessionView,
  RawSessionUser,
  SessionRole,
  IdentityStatus,
  PublicPlanView,
} from "./types";

type PlanResponse = components["schemas"]["PlanResponse"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function textField(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function nullableText(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function normalizeSessionRole(
  rawRole: unknown,
  isAdmin: boolean,
  isMatrixBackoffice: boolean,
): SessionRole {
  if (rawRole === "admin" || rawRole === "support" || rawRole === "matrix_user")
    return rawRole;
  if (isMatrixBackoffice) return "matrix_user";
  if (isAdmin) return "admin";
  return "user";
}

function normalizeSessionUser(raw: RawSessionUser): SessionView["user"] {
  const email = textField(raw.email, textField(raw.login));
  const name = textField(raw.name, textField(raw.fullName, email));
  const isAdmin = raw.is_admin === true || raw.isAdmin === true;
  const isMatrixBackoffice =
    raw.is_matrix_backoffice === true || raw.isMatrixBackoffice === true;
  const user: SessionView["user"] = {
    id: textField(raw.id),
    email,
    name,
    is_admin: isAdmin,
    is_matrix_backoffice: isMatrixBackoffice,
    matrix_login: nullableText(raw.matrix_login ?? raw.matrixLogin),
    role: normalizeSessionRole(raw.role, isAdmin, isMatrixBackoffice),
    // 0088: sessão antiga em cache (ou backend anterior ao campo) cai no
    // legado BRL — degradar para a moeda de antes é sempre seguro; adivinhar
    // dólar não seria.
    brokerCurrency: asBrokerCurrency(
      typeof raw.brokerCurrency === "string" ? raw.brokerCurrency : null,
    ),
  };
  if (typeof raw.licenseState === "string")
    user.licenseState = raw.licenseState;
  return user;
}

function normalizeSession(raw: unknown): SessionView {
  if (!isRecord(raw) || !isRecord(raw.user))
    throw new Error("invalid session payload: expected object with user");
  return {
    user: normalizeSessionUser(raw.user),
    expiresAt: textField(raw.expiresAt),
    expiresInMs: typeof raw.expiresInMs === "number" ? raw.expiresInMs : 0,
  };
}

export async function fetchSession(): Promise<SessionView> {
  const result = await apiClient.GET("/api/v2/auth/session");
  return normalizeSession(unwrap(result));
}

export async function fetchAdminSession(
  opts: RequestInit = {},
): Promise<SessionView> {
  // /api/v2/auth/admin-session not yet in openapi.yaml — use raw fetch until schema is updated.
  const res = await fetch("/api/v2/auth/admin-session", {
    ...opts,
    credentials: "include",
  });
  if (!res.ok) throw new Error(`fetchAdminSession failed status=${res.status}`);
  return normalizeSession(await res.json());
}

export async function postLogin(
  login: string,
  password: string,
): Promise<unknown> {
  // #46 C46-LEMBRAR-ME-REMOVIDO: no `rememberMe` — every session uses the
  // single 30-day default TTL (#46 C46-COOKIE-MAX-AGE-30D).
  const result = await apiClient.POST("/api/v2/auth/login", {
    body: { login, password },
  });
  return unwrap(result);
}

export async function postLogout(): Promise<unknown> {
  const result = await apiClient.POST("/api/v2/auth/logout");
  return unwrap(result);
}

export async function postRenew(): Promise<unknown> {
  const result = await apiClient.POST("/api/v2/auth/renew");
  return unwrap(result);
}

export async function postIdentify(email: string): Promise<IdentityStatus> {
  const result = await apiClient.POST("/api/v2/auth/identify", {
    body: { login: email },
  });
  return unwrap(result) as IdentityStatus;
}

// TODO(auth-2fa-backend): backend `/api/v2/auth/verify-2fa` is not implemented
// yet (see backend/src/quartel/api/routes/auth.py — only `requires_2fa` flag
// exists in IdentityStatus today). Once the route lands, switch to
// `apiClient.POST("/api/v2/auth/verify-2fa", ...)` and wire the openapi schema.
// Returns `ok` so callers can distinguish "code accepted" from network errors
// without throwing on 4xx (UI surfaces the message inline).
export interface Verify2faResult {
  ok: boolean;
  status: number;
  message: string;
}

export async function verify2fa(
  code: string,
  opts: RequestInit = {},
): Promise<Verify2faResult> {
  try {
    const res = await fetch("/api/v2/auth/verify-2fa", {
      ...opts,
      method: "POST",
      credentials: "include",
      headers: jsonContentHeaders(opts.headers),
      body: JSON.stringify({ code }),
    });
    const text = await res.text().catch(() => "");
    const message = extract2faMessage(text);
    return { ok: res.ok, status: res.status, message };
  } catch {
    return { ok: false, status: 0, message: "Erro ao conectar" };
  }
}

function jsonContentHeaders(headersInit: HeadersInit | undefined): Headers {
  const headers = new Headers(headersInit);
  if (!headers.has("Content-Type"))
    headers.set("Content-Type", "application/json");
  return headers;
}

function extract2faMessage(text: string): string {
  if (!text.trim()) return "";
  try {
    const parsed: unknown = JSON.parse(text);
    if (typeof parsed === "object" && parsed !== null) {
      const obj = parsed as Record<string, unknown>;
      if (typeof obj.detail === "string") return obj.detail;
      if (typeof obj.message === "string") return obj.message;
    }
    return "";
  } catch {
    return text;
  }
}

export async function fetchPaymentLink(): Promise<unknown> {
  const result = await apiClient.GET("/api/v2/auth/payment-link");
  return unwrap(result);
}

function normalizePublicPlan(plan: PlanResponse): PublicPlanView {
  return {
    id: plan.id,
    slug: plan.slug,
    displayName: publicPlanDisplayName(plan),
    amountCents: publicPlanAmountCents(plan),
    description: publicPlanDescription(plan),
    periodDurationDays: plan.periodDurationDays,
    checkoutUrl: publicPlanCheckoutUrl(plan),
    tokensIncluded: publicPlanTokensIncluded(plan),
    visibility: publicPlanVisibility(plan),
    tier: plan.tier,
  };
}

function publicPlanRecord(plan: PlanResponse): Record<string, unknown> {
  return plan as unknown as Record<string, unknown>;
}

function publicPlanDisplayName(plan: PlanResponse): string {
  const value = publicPlanRecord(plan).displayName;
  return typeof value === "string" ? value : plan.name;
}

function publicPlanAmountCents(plan: PlanResponse): number {
  const value = publicPlanRecord(plan).amountCents;
  return typeof value === "number" ? value : plan.priceCents;
}

function publicPlanDescription(plan: PlanResponse): string | null {
  const value = publicPlanRecord(plan).description;
  return typeof value === "string" ? value : null;
}

function publicPlanCheckoutUrl(plan: PlanResponse): string | null {
  const value = publicPlanRecord(plan).checkoutUrl;
  return typeof value === "string" ? value : null;
}

function publicPlanTokensIncluded(plan: PlanResponse): number | null {
  const value = publicPlanRecord(plan).tokensIncluded;
  return typeof value === "number" ? value : null;
}

function publicPlanVisibility(plan: PlanResponse): "public" | "hidden" {
  return plan.visibility === "hidden" ? "hidden" : "public";
}

export async function fetchPublicPlans(): Promise<readonly PublicPlanView[]> {
  const result = await apiClient.GET("/api/v2/subscriptions/plans");
  return (unwrap(result) ?? []).map(normalizePublicPlan);
}
